<template>
  <v-select
    :model-value="modelValue"
    :items="filteredItems"
    :disabled="disabled"
    bg-color="white"
    menu-icon="icon-arrow-down-xs"
    item-value="id"
    item-title="name"
    :menu-props="{ maxHeight: '300' }"
    :placeholder="placeholder"
    :no-data-text="noDataText || undefined"
    :data-testid="dataTestId"
    density="compact"
    variant="outlined"
    multiple
    rounded
    hide-details
    :clearable="clearable"
    @update:model-value="$emit('update:modelValue', $event)"
    @update:menu="search = ''"
  >
    <template #selection="{ index }">
      <span v-if="index === 0" class="body2">{{ placeholder }}</span>
    </template>
    <template #label>
      <span v-if="modelValue?.length" class="filter-label">{{ modelValue.length }}</span>
    </template>
    <template #prepend-item>
      <cui-select-search-field
        v-model="search"
        :placeholder="$t('general.search')"
        :data-test-id="`${dataTestId}-search`"
      />
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" title="">
        <template #prepend="{ isActive }">
          <v-list-item-action start>
            <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
          </v-list-item-action>
        </template>

        <v-list-item-title
          :data-testid="`${dataTestId}-${item.raw.id}-item`"
          class="d-flex align-center label-popup-block"
        >
          <span class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
          <span>{{ item.raw.name }}</span>
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { CuiSelectSearchField, useCuiSelectSearch } from "@coronet/cui";

export interface LabelFilterItem {
  id: string;
  name: string;
  color: string;
}

/**
 * Generic label multi-select (device / user labels) with an in-dropdown search.
 * Renders a colour swatch + name per item; the model is an array of label ids.
 * `items` may be any object with `id` / `name` / `color` (e.g. DeviceLabel, UserLabel).
 */
export default defineComponent({
  components: { CuiSelectSearchField },
  props: {
    /** Selected label ids (v-model). */
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** Available labels; each needs `id`, `name` and `color`. */
    items: {
      type: Array as PropType<LabelFilterItem[]>,
      default: () => [],
    },
    /** Already-translated placeholder / selection label. */
    placeholder: {
      type: String,
      required: true,
    },
    /** Already-translated empty-state text; omitted when empty. */
    noDataText: {
      type: String,
      default: "",
    },
    /** Disables the select. */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Shows the clear (x) affordance. */
    clearable: {
      type: Boolean,
      default: false,
    },
    /** `data-testid` for the select; items derive `${dataTestId}-${name}-item`, search `${dataTestId}-search`. */
    dataTestId: {
      type: String,
      default: "labels-filter",
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const { search, filteredItems } = useCuiSelectSearch<LabelFilterItem>(
      () => props.items,
      (item) => item.name
    );

    return { search, filteredItems };
  },
});
</script>

<style scoped lang="scss">
.color-preview {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 2px;
}

.label-popup-block span {
  flex: unset;
}
</style>
